/**
 * Which sequencing run (library) each file came from, read off its FASTQ headers, and the files grouped
 * by run — the candidate sample pools of an order.
 */

/**
 * `full`: the header names the run unambiguously (Illumina instrument + run number + flowcell, ONT run id,
 * MGI flowcell, PacBio movie). `partial`: only a coarser identity survives (pre-CASAVA-1.8 Illumina has
 * instrument and lane but no run), so two runs on one instrument would look alike.
 */
export type RunKeyStrength = 'full' | 'partial'

export type RunKey = { key: string; strength: RunKeyStrength; format: string }

/*
 * Lane is left out everywhere: one library is routinely spread over several lanes of a flowcell, and
 * `bcl2fastq` without `--no-lane-splitting` writes one file per lane of the same sample.
 */
const CASAVA = /^([^:\s]+):(\d+):([^:\s]+):(\d+):(\d+):(\d+):(\d+)$/
const LEGACY_ILLUMINA = /^([^:\s#]+):(\d+):(\d+):(\d+):(\d+)(?:#[^/\s]*)?(?:\/[12])?$/
const ONT_RUN = /(?:^|\s)run_?id=([0-9a-fA-F]{8,})/
const ONT_FLOWCELL = /(?:^|\s)flow_?cell_?id=(\S+)/
const MGI = /^((?:V|E|FP|DP|CL)\d{6,})L(\d{1,2})C\d{3}R\d{3,}\d+(?:\/[12])?$/
const PACBIO = /^(m\d{5}[a-z]?_\d{6}_\d{6})\//
const ION_TORRENT = /^([A-Z0-9]{5}):(\d{5}):(\d{5})$/

const illumina = (id: string): RunKey | null => {
  const casava = CASAVA.exec(id)
  if (casava) {
    const [, instrument, run, flowcell] = casava
    return { key: `illumina:${instrument}:${run}:${flowcell}`, strength: 'full', format: 'illumina' }
  }
  const legacy = LEGACY_ILLUMINA.exec(id)
  if (legacy) {
    return { key: `illumina-legacy:${legacy[1]}`, strength: 'partial', format: 'illumina-legacy' }
  }
  return null
}

/**
 * The run a read header names, or `null` when it names none — SRA dumps (`SRR123.1 1 length=301`),
 * headers rewritten to sample labels (`SM1|SM1_7`), or anything else unrecognised. SRA dumps that kept
 * the original header after the accession are read through to it.
 */
export const parseRunKey = (header: string): RunKey | null => {
  const ont = ONT_RUN.exec(header)
  if (ont) {
    const flowcell = ONT_FLOWCELL.exec(header)?.[1]
    return { key: `ont:${ont[1].toLowerCase()}${flowcell ? `:${flowcell}` : ''}`, strength: 'full', format: 'ont' }
  }
  const [id = '', ...rest] = header.trim().split(/\s+/)
  const direct = illumina(id)
  if (direct) {
    return direct
  }
  const mgi = MGI.exec(id)
  if (mgi) {
    return { key: `mgi:${mgi[1]}`, strength: 'full', format: 'mgi' }
  }
  const pacbio = PACBIO.exec(id)
  if (pacbio) {
    return { key: `pacbio:${pacbio[1]}`, strength: 'full', format: 'pacbio' }
  }
  const ion = ION_TORRENT.exec(id)
  if (ion) {
    return { key: `iontorrent:${ion[1]}`, strength: 'partial', format: 'iontorrent' }
  }
  if (/^[SEDC]RR\d+(\.\d+)*$/.test(id) && rest.length > 0) {
    return illumina(rest[0])
  }
  return null
}

export type RunFile = { name: string; headers: readonly string[] }

export type FileRuns = {
  /** Distinct run keys among the file's headers, most frequent first. */
  keys: string[]
  strength: RunKeyStrength | null
  /** Share of the file's headers that named a run. */
  parsedShare: number
}

/** A file whose headers mostly name no run is not trusted with the few that do. */
const MIN_PARSED_SHARE = 0.9

export const fileRuns = (headers: readonly string[]): FileRuns => {
  const counts = new Map<string, number>()
  let partial = false
  let parsed = 0
  for (const header of headers) {
    const run = parseRunKey(header)
    if (!run) {
      continue
    }
    parsed++
    partial ||= run.strength === 'partial'
    counts.set(run.key, (counts.get(run.key) ?? 0) + 1)
  }
  const parsedShare = headers.length === 0 ? 0 : parsed / headers.length
  if (parsed === 0 || parsedShare < MIN_PARSED_SHARE) {
    return { keys: [], strength: null, parsedShare }
  }
  return {
    keys: [...counts].sort((a, b) => b[1] - a[1]).map(([key]) => key),
    strength: partial ? 'partial' : 'full',
    parsedShare
  }
}

export type SequencingRun = {
  /** Every run key of the group; more than one only when a file carries reads of several runs. */
  keys: string[]
  files: number[]
}

export type SequencingRunVerdict = 'SINGLE_RUN' | 'MULTIPLE_RUNS' | 'UNKNOWN'

export type SequencingRunResult = {
  verdict: SequencingRunVerdict
  confidence: 'high' | 'low' | 'none'
  reason: string
  runs: SequencingRun[]
  /** Files whose headers name no run, as indexes into the input. */
  unknown: number[]
  /** Files whose head already holds reads of more than one run. */
  mixed: number[]
}

/**
 * Groups files by the run their headers name. Files sharing any run key land in one group, so a file
 * concatenated from two runs merges them rather than being split. `MULTIPLE_RUNS` is the signal to offer
 * one pool per run; `UNKNOWN` whenever any file names no run, because such a file could belong to any
 * group. `low` confidence when some key is only `partial`.
 */
export const detectSequencingRuns = (files: readonly RunFile[]): SequencingRunResult => {
  const perFile = files.map((file) => fileRuns(file.headers))
  const unknown = perFile.flatMap((f, i) => (f.keys.length === 0 ? [i] : []))
  const mixed = perFile.flatMap((f, i) => (f.keys.length > 1 ? [i] : []))

  const parent = new Map<string, string>()
  const find = (key: string): string => {
    let root = key
    for (let next = parent.get(root); next !== undefined && next !== root; next = parent.get(root)) {
      root = next
    }
    parent.set(key, root)
    return root
  }
  for (const f of perFile) {
    for (const key of f.keys) {
      if (!parent.has(key)) {
        parent.set(key, key)
      }
    }
    for (const key of f.keys.slice(1)) {
      parent.set(find(key), find(f.keys[0]))
    }
  }
  const groups = new Map<string, SequencingRun>()
  perFile.forEach((f, i) => {
    if (f.keys.length === 0) {
      return
    }
    const root = find(f.keys[0])
    const group = groups.get(root) ?? { keys: [], files: [] }
    group.files.push(i)
    for (const key of f.keys) {
      if (!group.keys.includes(key)) {
        group.keys.push(key)
      }
    }
    groups.set(root, group)
  })
  const runs = [...groups.values()].sort((a, b) => b.files.length - a.files.length)

  if (files.length === 0) {
    return { verdict: 'UNKNOWN', confidence: 'none', reason: 'no files', runs, unknown, mixed }
  }
  if (unknown.length > 0) {
    return {
      verdict: 'UNKNOWN',
      confidence: 'none',
      reason: `${unknown.length} of ${files.length} files carry no recognisable run in their headers (renamed or SRA-style)`,
      runs,
      unknown,
      mixed
    }
  }
  const confidence = perFile.some((f) => f.strength === 'partial') ? 'low' : 'high'
  const weak = confidence === 'low' ? ' (headers name the instrument only, not the run)' : ''
  if (runs.length === 1) {
    return { verdict: 'SINGLE_RUN', confidence, reason: `all files from one sequencing run${weak}`, runs, unknown, mixed }
  }
  return { verdict: 'MULTIPLE_RUNS', confidence, reason: `files from ${runs.length} sequencing runs${weak}`, runs, unknown, mixed }
}
